import { useMemo, useState } from "react";

import { formatAvgPerDay, reportingCalendarDayCount } from "../../lib/dashboardTime.js";
import { formatGbpPence } from "../../lib/formatMoney.js";
import type {
  AcquisitionData,
  DashboardTabHandler,
  FilterState,
  OpenRawEventsHandler,
} from "../../types.js";
import { ChartCard } from "../ChartCard.js";
import type { BarChartItem } from "../charts/BarChart.js";
import { BarChart } from "../charts/BarChart.js";

const STAGE_FILLS = [
  "#8b7cf6", // Interaction Stints
  "#5aa2ff", // Table QR Code
  "#f472b6", // Mobile Pageview
  "var(--color-warn)", // Payment Attempted
  "#31c48d", // Payment Confirmed
] as const;

const STAGE_LABELS = [
  "Interaction Stints",
  "Table QR Code*",
  "Mobile Pageview",
  "Payment Attempted",
  "Payment Confirmed",
] as const;

const LEGEND = [
  { label: "Stints", fill: STAGE_FILLS[0] },
  { label: "Table QR*", fill: STAGE_FILLS[1] },
  { label: "Mobile PV", fill: STAGE_FILLS[2] },
  { label: "Attempted", fill: STAGE_FILLS[3] },
  { label: "Confirmed", fill: STAGE_FILLS[4] },
];

type SortKey =
  | "interactionStints"
  | "tableQrCode"
  | "mobilePageview"
  | "paymentAttempted"
  | "paymentSucceeded"
  | "grossRevPence"
  | "venueName";

type VenueFunnelRow = AcquisitionData["paymentFunnelByVenue"][number];

function stageCounts(row: VenueFunnelRow): number[] {
  return [
    row.interactionStints,
    row.tableQrCode,
    row.mobilePageview,
    row.paymentAttempted,
    row.paymentSucceeded,
  ];
}

function fmtPctOf(n: number, base: number): string {
  if (base <= 0) return "—";
  return `${Math.round((n / base) * 100)}%`;
}

const CRITERIA = `Same Payment Flow Funnel steps as Overview / Retention, broken out by **venues.name** via **devices.venue_id** (one bar per venue; typically one table per venue).

0) Interaction stints — distinct non-empty **interaction_session_id** (dashboard filters **including Game**).
1–4) Payment steps — date + venue + device only (**no Game** filter):
   1) popup_open where popup = payment — count(distinct interaction_session_id)
   2) Mobile Pageview — distinct live play sessions (page_opened / legacy /pay%; session created ≤30 min before open + play_session_payments row). Tooltip includes raw event count.
   3) Payment Attempted — distinct live play sessions with express_checkout_pressed (raw count in tooltip).
   4) payment_confirmed — distinct checkout_id (fallback: row count) and Gross Rev. = sum of effective confirmed amounts.

Bars are **overlay** (nested): each stage rises from the baseline to its count; bar height = Interaction Stints. Segment labels show count; % is vs that venue’s Interaction Stints.

Segment click: Stints → Sessions tab (venue filter set); other stages → Raw Events with venue filter + the step’s event_name / Properties. Middle-click / Ctrl/Cmd+click: new tab.

Date range and venue filter use the dashboard Filter bar (no duplicate controls on this card).`;

interface PaymentFunnelByVenueCardProps {
  rows: AcquisitionData["paymentFunnelByVenue"];
  filters: FilterState;
  reportingTimezone: string;
  onOpenRawEvents: OpenRawEventsHandler;
  onOpenSessions: DashboardTabHandler;
}

export function PaymentFunnelByVenueCard({
  rows,
  filters,
  reportingTimezone,
  onOpenRawEvents,
  onOpenSessions,
}: PaymentFunnelByVenueCardProps) {
  const [sortKey, setSortKey] = useState<SortKey>("interactionStints");

  const sorted = useMemo(() => {
    const copy = [...rows];
    copy.sort((a, b) => {
      if (sortKey === "venueName") return a.venueName.localeCompare(b.venueName);
      return (b[sortKey] as number) - (a[sortKey] as number) || a.venueName.localeCompare(b.venueName);
    });
    return copy;
  }, [rows, sortKey]);

  const totals = useMemo(() => {
    return rows.reduce(
      (acc, r) => {
        acc.interactionStints += r.interactionStints;
        acc.mobilePageviewEvents += r.mobilePageviewEvents;
        acc.paymentAttemptedEvents += r.paymentAttemptedEvents;
        acc.tableQrCode += r.tableQrCode;
        acc.mobilePageview += r.mobilePageview;
        acc.paymentAttempted += r.paymentAttempted;
        acc.paymentSucceeded += r.paymentSucceeded;
        acc.grossRevPence += r.grossRevPence;
        return acc;
      },
      {
        interactionStints: 0,
        tableQrCode: 0,
        mobilePageview: 0,
        mobilePageviewEvents: 0,
        paymentAttempted: 0,
        paymentAttemptedEvents: 0,
        paymentSucceeded: 0,
        grossRevPence: 0,
      },
    );
  }, [rows]);

  const reportingDays = reportingCalendarDayCount(filters.from, filters.to, reportingTimezone);
  const stintsBase = totals.interactionStints;

  const bars: BarChartItem[] = sorted.map((row) => {
    const counts = stageCounts(row);
    const stints = Math.max(row.interactionStints, 0);
    const barMax = Math.max(stints, ...counts, 1);
    return {
      label: row.venueName || row.venueId.slice(0, 8) || "Venue",
      value: barMax,
      valueLabel: stints > 0 ? String(stints) : "0",
      stack: counts.map((value, si) => ({
        value: Math.max(0, value),
        fill: STAGE_FILLS[si]!,
        label:
          stints > 0
            ? `${Math.round(value)} (${fmtPctOf(value, stints)})`
            : String(Math.round(value)),
      })),
    };
  });

  const openStage = (
    row: VenueFunnelRow,
    stageIndex: number,
    e?: Pick<MouseEvent, "button" | "ctrlKey" | "metaKey">,
  ) => {
    const venueId = row.venueId || undefined;
    if (stageIndex === 0) {
      onOpenSessions(e, venueId ? { venueId } : undefined);
      return;
    }
    if (stageIndex === 1) {
      onOpenRawEvents(
        {
          eventName: "popup_open",
          propertiesContains: `"popup":"payment"`,
          ...(venueId ? { venueId } : {}),
        },
        e,
      );
      return;
    }
    if (stageIndex === 2) {
      onOpenRawEvents(
        {
          eventName: "screen_interacted",
          propertiesContains: `"screen_name":"mobile_payment"`,
          propertiesContains2: `"interaction_type":"page_opened"`,
          ...(venueId ? { venueId } : {}),
        },
        e,
      );
      return;
    }
    if (stageIndex === 3) {
      onOpenRawEvents(
        {
          eventName: "screen_interacted",
          propertiesContains: `"screen_name":"mobile_payment"`,
          propertiesContains2: `"interaction_type":"express_checkout_pressed"`,
          ...(venueId ? { venueId } : {}),
        },
        e,
      );
      return;
    }
    onOpenRawEvents(
      {
        eventName: "payment_confirmed",
        ...(venueId ? { venueId } : {}),
      },
      e,
    );
  };

  const summaryItems: Array<{ name: string; value: string; sub: string }> = [
    {
      name: "Total Interactions",
      value: stintsBase.toLocaleString(),
      sub: `${formatAvgPerDay(stintsBase, reportingDays)} /day`,
    },
    {
      name: "QR Code Scans",
      value: totals.tableQrCode.toLocaleString(),
      sub: `${fmtPctOf(totals.tableQrCode, stintsBase)} of stints`,
    },
    {
      name: "Mobile Pageviews",
      value: totals.mobilePageview.toLocaleString(),
      sub: `${fmtPctOf(totals.mobilePageview, stintsBase)} of stints`,
    },
    {
      name: "Payment Attempts",
      value: totals.paymentAttempted.toLocaleString(),
      sub: `${fmtPctOf(totals.paymentAttempted, stintsBase)} of stints`,
    },
    {
      name: "Payments Succeeded",
      value: totals.paymentSucceeded.toLocaleString(),
      sub: `${fmtPctOf(totals.paymentSucceeded, stintsBase)} of stints`,
    },
    {
      name: "Gross Revenue",
      value: formatGbpPence(totals.grossRevPence),
      sub:
        reportingDays > 0
          ? `${formatGbpPence(Math.round(totals.grossRevPence / reportingDays))} /day avg`
          : "—",
    },
  ];

  return (
    <ChartCard
      title="Payment Funnel by Venue"
      criteria={CRITERIA}
      note="One bar per venue (typically one table). Overlay stages: Interaction Stints → Table QR* → Mobile Pageview → Payment Attempted → Payment Succeeded. % vs that venue’s Interaction Stints. Date / venue filters: dashboard Filter bar."
      titleRight={
        <label className="flex shrink-0 items-center gap-1.5 text-[12px] text-text-secondary">
          Sort
          <select
            className="rounded border border-border bg-panel-2 px-1.5 py-0.5 text-[12px] text-text-primary"
            value={sortKey}
            onChange={(e) => setSortKey(e.target.value as SortKey)}
          >
            <option value="interactionStints">Interactions (high → low)</option>
            <option value="tableQrCode">QR scans</option>
            <option value="mobilePageview">Mobile pageviews</option>
            <option value="paymentAttempted">Payment attempts</option>
            <option value="paymentSucceeded">Payments succeeded</option>
            <option value="grossRevPence">Gross revenue</option>
            <option value="venueName">Venue name</option>
          </select>
        </label>
      }
    >
      <div className="mb-3 grid grid-cols-2 gap-2 sm:grid-cols-3 xl:grid-cols-6">
        {summaryItems.map((item) => (
          <div key={item.name} className="rounded-lg border border-border bg-panel-2 px-2.5 py-2">
            <div className="text-[11px] text-text-secondary">{item.name}</div>
            <div className="text-[16px] font-semibold tabular-nums text-text-primary">{item.value}</div>
            <div className="text-[11px] text-[#a7badf]">{item.sub}</div>
          </div>
        ))}
      </div>

      {bars.length === 0 ? (
        <p className="py-5 text-[13px] text-text-secondary">No venue funnel activity in this range</p>
      ) : (
        <BarChart
          data={bars}
          fillBars
          stackOverlay
          verbatimAxisLabels
          rotateAxisLabels={bars.length > 6}
          axisLabelMaxChars={14}
          maxBarWidth={72}
          legend={LEGEND}
          hideStackSegmentLabels={bars.length > 8}
          barTooltip={(item, index, segmentIndex) => {
            const row = sorted[index];
            if (!row || segmentIndex === undefined) return item.label;
            const counts = stageCounts(row);
            const n = counts[segmentIndex] ?? 0;
            const label = STAGE_LABELS[segmentIndex] ?? "Stage";
            const pct = fmtPctOf(n, row.interactionStints);
            const hints = [
              "Click: Sessions tab for this venue.",
              "Click: Raw Events → popup_open (payment).",
              `Distinct live play sessions. Raw page_opened events: ${row.mobilePageviewEvents}. Click: Raw Events → mobile_payment page_opened.`,
              `Distinct live play sessions. Raw express_checkout_pressed: ${row.paymentAttemptedEvents}. Click: Raw Events → express_checkout_pressed.`,
              "Click: Raw Events → payment_succeeded.",
            ];
            return `${row.venueName} · ${label}: ${n.toLocaleString()} (${pct} of stints). ${hints[segmentIndex] ?? ""} Middle-click / Ctrl/Cmd+click: new tab.`;
          }}
          onBarClick={(item, index, segmentIndex, e) => {
            const row = sorted[index];
            if (!row || segmentIndex === undefined) return;
            openStage(row, segmentIndex, e);
          }}
        />
      )}
    </ChartCard>
  );
}
